<script setup>
import { inject, ref, watch } from 'vue';
import { 
	apiModifierFormuleChamp, 
	apiModifierFormuleImport,
	apiModifierFormuleRoutine } from '@/services/ServiceModele.js';
import Dialog from 'primevue/dialog';
import FormuleAffichee from '@/components/generiques/FormuleAffichee.vue';
import FormuleEditee from '@/components/generiques/FormuleEditee.vue';
import AideFormuleImport from '@/components/generiques/AideFormuleImport.vue';

const { conteneurFormule } = defineProps({
    conteneurFormule: Object
});

const emit = defineEmits(['fermer']);

const getTypeObjet = inject('getTypeObjet');
const appelerApiModifModele = inject('appelerApiModifModele');
const afficherSucces = inject('afficherSucces');
const afficherErreur = inject('afficherErreur');

const modeEdition = ref(false);
const estMinimisee = ref(false);
const composantFenetre = ref(null);

watch(() => conteneurFormule.formule.demandeFocus, (demandeFocus) => {
    delete conteneurFormule.formule.demandeFocus;
    if (demandeFocus) {
        composantFenetre.value.focus();
        if (demandeFocus.modeEdition) {
            modeEdition.value = true;
        }
    }
});

function fermerFenetre() {
    emit('fermer');
}

function setModeEdition(edition) {
    modeEdition.value = edition;
    if (edition) {
        setMinimisee(false);
    }
    if (composantFenetre.value) {
        composantFenetre.value.focus();
    }
}

function setMinimisee(min) {
    estMinimisee.value = min;
}

function getDescriptionFormule(conteneurFormule) {
    var descTypeConteneur;
    var descConteneur;
    switch (conteneurFormule.type) {
        case 'ChampOriginal':
            descTypeConteneur = 'Champ';
            descConteneur = `${getTypeObjet(conteneurFormule.idTypeObjet).nom}.${conteneurFormule.nom}`;
            break;
        case 'ChampLien':
            descTypeConteneur = 'Champ de lien';
            descConteneur = `${getTypeObjet(conteneurFormule.idTypeObjet).nom}.${conteneurFormule.nom}`;
            break;
        case 'ChampSurcharge':
            descTypeConteneur = 'Champ (surcharge)';
            descConteneur = `${getTypeObjet(conteneurFormule.idTypeObjet).nom}.${conteneurFormule.nom}`;
            break;
        case 'ChampPopulation':
            descTypeConteneur = 'Population';
            descConteneur = `<${conteneurFormule.nom}>`;
            break;
        case 'ChampDimension':
            descTypeConteneur = 'Dimension';
            descConteneur = `<${conteneurFormule.nom}>`;
            break;
        case 'ImportDonnees':
            descTypeConteneur = 'Import';
            descConteneur = conteneurFormule.nom;
            break;
        case 'Routine':
            descTypeConteneur = 'Routine';
            descConteneur = conteneurFormule.nom;
            break;
    }
    const asterisqueModif = (modeEdition.value ? '(*)' : '');
    return `${descTypeConteneur} : ${descConteneur} ${asterisqueModif}`;
}

function demanderEnregistrement() {
    conteneurFormule.demandeEnregistrement = true;
}

function enregistrer(texteFormule) {
    modifierFormule(conteneurFormule, texteFormule);
}

function modifierFormule(conteneurFormule, texte) {
	if (conteneurFormule.type.startsWith('Champ')) {
		appelerApiModifModele(apiModifierFormuleChamp, conteneurFormule, texte, (reponseOK) => {
			if (reponseOK) {
                if (!conteneurFormule.formuleEnErreur) {
                    afficherSucces('Formule modifiée');
                    fermerFenetre();
                } else {
                    afficherErreur('La formule modifiée est en erreur');
                    setModeEdition(false);
                }
			}
		});
	} else if (conteneurFormule.type === 'ImportDonnees') {
		appelerApiModifModele(apiModifierFormuleImport, conteneurFormule, texte, (reponseOK) => {
			if (reponseOK) {
                if (!conteneurFormule.formuleEnErreur) {
                    afficherSucces('Formule modifiée');
                    fermerFenetre();
                } else {
                    afficherErreur('La formule modifiée est en erreur');
                    setModeEdition(false);
                }
			}
		});
	} else if (conteneurFormule.type === 'Routine') {
		appelerApiModifModele(apiModifierFormuleRoutine, conteneurFormule, texte, (reponseOK) => {
			if (reponseOK) {
                if (!conteneurFormule.formuleEnErreur) {
                    afficherSucces('Formule modifiée');
                    fermerFenetre();
                } else {
                    afficherErreur('La formule modifiée est en erreur');
                    setModeEdition(false);
                }
			}
		});
	}
}

</script>

<template>
    <Dialog 
    class="fenetre-formule"
    ref="composantFenetre"
    :visible="true"
    tabindex="0"
    :keepInViewport="false"
    :maximizable="!estMinimisee"
    @update:visible="fermerFenetre">
        <template #header>
            <div class="zone-titre-fenetre">
                <div class="zone-desc-formule">
                    <i class="fas fa-square-root-variable icone-formule"></i>
                    <label class="titre">{{ getDescriptionFormule(conteneurFormule) }}</label>
                    <div class="zone-boutons boutons-edition-formule">
                        <button 
                            v-if="!modeEdition && !estMinimisee"
                            autofocus
                            class="bouton btn-primaire fas fa-edit" 
                            v-tooltip="'Editer la formule'"
                            @click="setModeEdition(true)" />
                        <div v-else-if="!estMinimisee"
                            class="boutons-en-mode-edition">
                            <button class="bouton annuler fa-solid fa-undo" 
                                v-tooltip.bottom="'Annuler les modifications (Echap)'"
                                @click="setModeEdition(false)" />
                            <button class="bouton enregistrer fa-solid fa-check" 
                                v-tooltip.bottom="'Enregistrer (Shift + Entrée)'"
                                @click="demanderEnregistrement()" />
                        </div>
                    </div>
                </div>
                <div class="zone-boutons">
                    <button 
                        v-if="!estMinimisee"
                        class="btn-action-fenetre fas fa-down-left-and-up-right-to-center" 
                        v-tooltip="'Minimiser la formule'"
                        @click="setMinimisee(true)" />
                    <button 
                        v-else
                        class="btn-action-fenetre fas fa-up-right-and-down-left-from-center" 
                        v-tooltip="'Afficher la formule'"
                        @click="setMinimisee(false)" />
                </div>
            </div>
        </template>
        <div v-if="!estMinimisee" class="zone-formule">
            <template v-if="!modeEdition">
                <AideFormuleImport 
                    v-if="conteneurFormule.type === 'ImportDonnees'"
                    :import-donnees="conteneurFormule"
                    @fermeture-fenetre="fermerFenetre" />
                <FormuleAffichee 
                    v-if="!modeEdition"
                    :class="{'formule-en-erreur': conteneurFormule.formuleEnErreur}"
                    :formule="conteneurFormule?.formule"
                    :affichage-suggestions="true"
                    @double-clic="setModeEdition(true)"
                    @modif-texte-formule="(texteFormule) => enregistrer(texteFormule)" />
            </template>
            <FormuleEditee
                v-else 
                class="panel-formule"
                :conteneur-formule="conteneurFormule"
                @annuler="setModeEdition(false)"
                @enregistrer="(texteFormule) => enregistrer(texteFormule)" />
        </div>
    </Dialog>
</template>

<style scoped>

.zone-titre-fenetre {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    margin-right: 5px;
}

.zone-desc-formule {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
}

.boutons-edition-formule {
    padding-left: 20px;
}

.contenu-panel-formule {
    border: 1px solid var(--couleur-bordure);
    padding: 5px;
    height: 20vh;
}

.boutons-en-mode-edition {
	display: flex;
	gap: 2px;
}

.annuler {
	color: var(--couleur-annuler);
	width: 22px;
}

.enregistrer {
	color: var(--couleur-valider);
	width: 22px;
}

.zone-formule {
    width: 60vw;
}

.panel-formule {
    border: 1px solid var(--couleur-bordure);
}

.icone-formule {
    margin-right: 10px;
    font-size: 16px;
    color: var(--couleur-primaire);
}

.zone-boutons {
    display: flex;
    flex-direction: row;
    gap: 5px;
    margin-left: 20px;
}

.btn-action-fenetre {
    border: none;
    background: transparent;
    color: #6c757d;
    margin-right: -20px;
}

.panel-formule {
    background-color: var(--couleur-fond-formule);    
    min-height: 30vh;
}

</style>